import Vue from "vue";
import VueRouter from "vue-router";
// import { getEnvironmentVariables } from "@/common/utils/env/envSettings.js";
import { checkAccess } from "@/common/utils/auth/authRules";
import { constants } from "@/lib/CONSTANTS.js";

// Function to check if the current URL is from Azure portal
function isAzurePortalUrl() {
  if (typeof window !== "undefined" && window.location) {
    const currentHost = window.location.hostname;
    // Check if it's an Azure deployment (azurewebsites.net or its subdomains)
    return currentHost === "azurewebsites.net" || currentHost.endsWith(".azurewebsites.net");
  }
  return false;
}

Vue.use(VueRouter);

function loadView(view) {
  // This doesn't use template literals because Jest complains it can't map the view for coverage
  return () => import(/* webpackChunkName: "view-[request]" */ "@/views/" + view + ".vue");
}

function metaTitleParamMapper(to) {
  const metaMaps = {}; //meta: param
  let _title = to.meta.title,
    title_fragment = _title;
  if (metaMaps) {
    for (let map in metaMaps) {
      title_fragment =
        _title.includes(map) && Object.prototype.hasOwnProperty.call(to.params, metaMaps[map])
          ? _title.replace(map, to.params[metaMaps[map]])
          : _title;
    }
  }
  return title_fragment;
}

const generalRouterDetails = [
  {
    path: "/",
    name: "home",
    component: loadView("Home"),
    meta: { scrollToTop: true, title: "Home", disableRoute: true }, //Disabled - Temporarily
    redirect: () => {
      //Since disabled redirecting to exceptions page
      return "/exceptions";
    }
  },
  {
    path: "/exceptions",
    name: "exceptions",
    component: loadView("Exceptions"),
    meta: { scrollToTop: true, title: "Exceptions" }
  },
  // {
  //   path: "/port-mismatch",
  //   name: "port-mismatch",
  //   component: loadView("PortMismatch"),
  //   meta: { scrollToTop: true, title: "Port mismatch", disableRoute: true } //Disabled - Temporarily
  // },
  {
    path: "/port-call",
    name: "port-call",
    component: loadView("PortCall"),
    meta: { scrollToTop: true, title: "Port call" }
  },
  {
    path: "/manifest",
    name: "manifest",
    component: loadView("Manifest"),
    meta: { scrollToTop: true, title: "Manifest" }
  },
  {
    path: "/releases/:type",
    name: "releases",
    component: loadView("Releases"),
    meta: { scrollToTop: true, title: "Releases" }
  },
  {
    path: "/notification",
    name: "notification center",
    component: loadView("NotificationCenter"),
    meta: { scrollToTop: true, title: "Notification Center" }
  },
  // {
  //   path: "/vessel-voyage",
  //   name: "vessel-voyage",
  //   component: loadView("VesselVoyage"),
  //   meta: { scrollToTop: true, title: "Vessel Voyage", disableRoute: true } //Disabled - Temporarily
  // },
  {
    path: "/consignment",
    name: "consignment",
    component: loadView("BillOfLading"),
    meta: { scrollToTop: true, title: "Consignment" },
    props: true,
    children: [
      {
        name: "bol-1",
        path: ":billofLadingId",
        component: loadView("BillOfLading"),
        meta: { scrollToTop: true, title: "Consignment" }
      }
    ]
  },
  {
    path: "/bol",
    name: "bol",
    component: loadView("BillOfLading"),
    meta: { scrollToTop: true, title: "Bill of Lading" },
    props: true,
    children: [
      {
        name: "bol-1",
        path: ":billofLadingId",
        component: loadView("BillOfLading"),
        meta: { scrollToTop: true, title: "Bill of Lading" }
      }
    ]
  },
  {
    path: "/oldbol",
    name: "oldBol",
    component: loadView("Bol"),
    meta: { scrollToTop: true, title: "Bill of Lading" },
    props: true,
    children: [
      {
        name: "bol-1",
        path: ":billofLadingId",
        component: loadView("Bol"),
        meta: { scrollToTop: true, title: "Bill of Lading" }
      }
    ]
  },
  {
    path: "*",
    redirect: "/"
  }
];

const maintanenceEnabledRouter = [
  {
    path: "/",
    name: "home",
    component: loadView("Home"),
    meta: { scrollToTop: true, title: "Home", disableRoute: true }, //Disabled - Temporarily
    redirect: () => {
      return "/maintanence";
    }
  },
  {
    path: "/maintanence",
    name: "Maintanence",
    component: loadView("Maintanence"),
    meta: { scrollToTop: true, title: "Maintanence" }
  },
  {
    path: "*",
    redirect: "/"
  }
];

const azureUrlMigrationRouter = [
  {
    path: "/",
    name: "home",
    component: loadView("Home"),
    meta: { scrollToTop: true, title: "Home", disableRoute: true }, //Disabled - Temporarily
    redirect: () => {
      return "/azure-url-migration";
    }
  },
  {
    path: "/azure-url-migration",
    name: "AzureUrlMigration",
    component: loadView("AzureUrlMigration"),
    meta: { scrollToTop: true, title: "URL Migration" }
  },
  {
    path: "*",
    redirect: "/"
  }
];
// Function to determine which router to use based on configurations
function getRouterConfiguration() {
  // Check if maintenance mode is enabled first
  if (constants.pageSettings.isMaintanenceON) {
    return maintanenceEnabledRouter;
  }

  // Check if Azure URL redirect is enabled and current URL is from Azure portal
  if (constants.pageSettings.isAzureUrlRedirectON && isAzurePortalUrl()) {
    return azureUrlMigrationRouter;
  }

  // Default to general router
  return generalRouterDetails;
}

const router = new VueRouter({
  mode: "history",
  base: "/",
  // See https://router.vuejs.org/guide/advanced/scroll-behavior.html
  // Provides smooth scrolling back-to-top behaviour on page transition
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { x: 0, y: 0, behaviour: "smooth" };
    }
  },
  routes: getRouterConfiguration()
});

// Export function to set up router guard after MSAL initialization
export function setupRouterGuard() {
  router.beforeEach((to, from, next) => {
    document.title = `GCD${
      to && to.name !== "home" ? " - " + metaTitleParamMapper(to) : ""
    } | Keeps tracks of shipment`;
    try {
      // If localhost, just carry on, otherwise, check the access
      // if (getEnvironmentVariables().label !== "localhost") {
      if (to.matched.some(record => record.meta.disableRoute)) {
        next({ path: "/" });
      } else {
        checkAccess(next);
      }

      // } else {
      //   next();
      // }
    } catch (e) {
      // console.error("Technical Error Occured" + e);
    }
  });
}

// error handler:
router.onError(() => {
  // the build for production does not allow console logs ....
  // console.error("Handling this error", err.message);
});

export default router;
